<script>
/**
 * @component ProviderNetworkList
 * @description ABM de red de prestadores institucionales: búsqueda, filtros por especialidad y tipo,
 *              tabla paginada y dialog de alta/edición.
 * @example <ProviderNetworkList />
 */
import providerNetworkService from "@/services/provider-network";
import Spinner from "@/components/ui/Spinner";
import debounce from "debounce";
import { mapGetters } from "vuex";

const DEBOUNCE_MS = 500;

const TYPE_OPTIONS = [
  { value: "individual", label: "Individual" },
  { value: "institution", label: "Institución" },
];

const STATUS_OPTIONS = [
  { value: "active", label: "Activo" },
  { value: "inactive", label: "Inactivo" },
  { value: "suspended", label: "Suspendido" },
];

export default {
  name: "ProviderNetworkList",

  components: { Spinner },

  data() {
    return {
      isLoading: false,
      searchInput: "",
      filterType: "",
      filterSpecialty: "",
      providers: [],
      total: 0,
      currentPage: 1,
      pageSize: 20,
      typeOptions: TYPE_OPTIONS,
      statusOptions: STATUS_OPTIONS,
      isDialogVisible: false,
      dialogMode: "create",
      editingId: null,
      form: {
        cuit: "",
        businessName: "",
        type: "individual",
        specialties: "",
        contract: "",
        status: "active",
      },
      formErrors: {},
    };
  },

  computed: {
    ...mapGetters(["isAdmin"]),
  },

  created() {
    this.fetchProviders();
  },

  methods: {
    /**
     * @description Obtiene los prestadores del backend
     */
    async fetchProviders() {
      this.isLoading = true;
      try {
        const params = {
          offset: (this.currentPage - 1) * this.pageSize,
          limit: this.pageSize,
        };
        if (this.searchInput) params.searchInput = this.searchInput;
        if (this.filterType) params.type = this.filterType;
        if (this.filterSpecialty) params.specialty = this.filterSpecialty;

        const result = await providerNetworkService.query(params);
        this.providers = result.docs || [];
        this.total = result.total || 0;
      } catch (error) {
        this.$message.error("Error al cargar prestadores");
        console.error(error);
      } finally {
        this.isLoading = false;
      }
    },

    debounceSearch: debounce(function () {
      this.currentPage = 1;
      this.fetchProviders();
    }, DEBOUNCE_MS),

    onFilterChange() {
      this.currentPage = 1;
      this.fetchProviders();
    },

    onPageChange(page) {
      this.currentPage = page;
      this.fetchProviders();
    },

    getTypeLabel(type) {
      const opt = TYPE_OPTIONS.find((o) => o.value === type);
      return opt ? opt.label : type || "-";
    },

    getStatusLabel(status) {
      const opt = STATUS_OPTIONS.find((o) => o.value === status);
      return opt ? opt.label : status || "-";
    },

    getStatusBadge(status) {
      const map = {
        active: "badge--success",
        inactive: "badge--danger",
        suspended: "badge--warning",
      };
      return map[status] || "badge--neutral";
    },

    openDialog(mode = "create", provider = null) {
      this.dialogMode = mode;
      this.isDialogVisible = true;
      this.formErrors = {};
      if (mode === "edit" && provider) {
        this.editingId = provider._id;
        this.form = {
          cuit: provider.cuit || "",
          businessName: provider.businessName || "",
          type: provider.type || "individual",
          specialties: Array.isArray(provider.specialties)
            ? provider.specialties.join(", ")
            : provider.specialties || "",
          contract: provider.contract || "",
          status: provider.status || "active",
        };
      } else {
        this.resetForm();
      }
    },

    closeDialog() {
      this.isDialogVisible = false;
      this.resetForm();
    },

    resetForm() {
      this.editingId = null;
      this.form = {
        cuit: "",
        businessName: "",
        type: "individual",
        specialties: "",
        contract: "",
        status: "active",
      };
      this.formErrors = {};
    },

    async saveProvider() {
      this.formErrors = {};
      if (!this.form.cuit) this.formErrors.cuit = "CUIT requerido";
      if (!this.form.businessName)
        this.formErrors.businessName = "Razón social requerida";
      if (Object.keys(this.formErrors).length) return;

      try {
        const data = {
          ...this.form,
          specialties: this.form.specialties
            .split(",")
            .map((s) => s.trim())
            .filter(Boolean),
        };
        if (this.dialogMode === "create") {
          await providerNetworkService.create(data);
          this.$message.success("Prestador creado");
        } else {
          await providerNetworkService.update(this.editingId, data);
          this.$message.success("Prestador actualizado");
        }
        this.closeDialog();
        this.fetchProviders();
      } catch (error) {
        this.$message.error("Error al guardar prestador");
      }
    },

    async deleteProvider(provider) {
      try {
        await this.$confirm(
          `¿Eliminar al prestador "${provider.businessName}"?`,
          "Eliminar",
          {
            confirmButtonText: "Aceptar",
            cancelButtonText: "Cancelar",
            type: "warning",
          }
        );
        await providerNetworkService.remove(provider._id);
        this.$message.success("Prestador eliminado");
        this.fetchProviders();
      } catch {
        // Cancelado
      }
    },
  },
};
</script>

<template lang="pug">
section
  header.headline
    .headline__actions
      el-input.search__input(
        prefix-icon="el-icon-search"
        v-model="searchInput"
        @input="debounceSearch"
        placeholder="Buscar por nombre o CUIT"
        :clearable="true"
        size="small"
      )
      el-select(
        v-model="filterType"
        placeholder="Tipo"
        clearable
        @change="onFilterChange"
        size="small"
      )
        el-option(
          v-for="opt in typeOptions"
          :key="opt.value"
          :label="opt.label"
          :value="opt.value"
        )
      .button.button--navy(@click="openDialog('create')" v-if="isAdmin")
        micon(name="add_circle_outline").button__icon
        span.button__text Nuevo Prestador

  .box.box--with-subnav
    .box__content--stretch
      table
        thead
          tr
            th CUIT
            th Razón Social
            th Tipo
            th Especialidades
            th Contrato
            th Estado
            th Acciones
        tbody
          spinner(v-if="isLoading")
          tr(
            v-else
            v-for="p in providers"
            :key="p._id"
            @click="openDialog('edit', p)"
          )
            td {{ p.cuit }}
            td {{ p.businessName }}
            td {{ getTypeLabel(p.type) }}
            td {{ Array.isArray(p.specialties) ? p.specialties.join(', ') : (p.specialties || '-') }}
            td {{ p.contract || '-' }}
            td
              span.badge(:class="getStatusBadge(p.status)") {{ getStatusLabel(p.status) }}
            td.actions
              .actions-container(@click.stop)
                button.button.button--action(@click="openDialog('edit', p)")
                  micon(name="edit")
                button.button.button--action(@click="deleteProvider(p)" v-if="isAdmin")
                  micon(name="delete_forever")

    el-pagination(
      v-if="total > pageSize"
      layout="prev, pager, next"
      :total="total"
      :page-size="pageSize"
      :current-page="currentPage"
      @current-change="onPageChange"
    )

  el-dialog(
    :title="dialogMode === 'create' ? 'Nuevo Prestador' : 'Editar Prestador'"
    :visible.sync="isDialogVisible"
    width="720px"
  )
    el-form(label-position="left" label-width="140px" size="small")
      el-row(:gutter="16")
        el-col(:span="12")
          el-form-item(label="CUIT" required :error="formErrors.cuit")
            el-input(v-model="form.cuit" maxlength="15" placeholder="Ej: 30-12345678-9")
        el-col(:span="12")
          el-form-item(label="Razón Social" required :error="formErrors.businessName")
            el-input(v-model="form.businessName" maxlength="100" placeholder="Ej: Sanatorio del Sur S.A.")
      el-row(:gutter="16")
        el-col(:span="12")
          el-form-item(label="Tipo")
            el-select(v-model="form.type" style="width: 100%" placeholder="Seleccionar tipo")
              el-option(
                v-for="opt in typeOptions"
                :key="opt.value"
                :label="opt.label"
                :value="opt.value"
              )
        el-col(:span="12")
          el-form-item(label="Estado")
            el-select(v-model="form.status" style="width: 100%" placeholder="Seleccionar estado")
              el-option(
                v-for="opt in statusOptions"
                :key="opt.value"
                :label="opt.label"
                :value="opt.value"
              )
      el-form-item(label="Especialidades")
        el-input(v-model="form.specialties" maxlength="200" placeholder="Ej: Cardiología, Traumatología, Clínica Médica")
      el-form-item(label="Contrato")
        el-input(v-model="form.contract" maxlength="100" placeholder="Ej: CONT-2024-001")
    template(#footer)
      el-button(@click="closeDialog") Cancelar
      el-button(type="primary" @click="saveProvider") Guardar
</template>

<style lang="scss" scoped>
.headline__actions {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}
.search__input {
  width: 280px;
}
.el-select {
  width: 160px;
}
tbody td:hover {
  cursor: pointer;
}
</style>
